import React, { useState, useEffect } from 'react';
import useOverSizeList from './useManageOverSize';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Alert,
  Grid,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const OverSizeList = () => {
  const shopName = useSelector((state) => state.shop.name);

  const [url, setUrl] = useState('');
  const [searchUrl, setSearchUrl] = useState('');
  const [filteredLists, setFilteredLists] = useState([]);
  
  const { 
    loading, 
    error, 
    overSizeLists, 
    registerOverSizeList, 
    deleteOverSizeList,
    fetchOverSizeLists
  } = useOverSizeList();

  useEffect(() => {
    fetchOverSizeLists();
  }, [fetchOverSizeLists]);

  // Filter lists based on search URL
  useEffect(() => {
    if (!searchUrl.trim()) {
      setFilteredLists(overSizeLists);
    } else {
      const filtered = overSizeLists.filter(item => 
        item.url && item.url.toLowerCase().includes(searchUrl.toLowerCase())
      );
      setFilteredLists(filtered);
    }
  }, [overSizeLists, searchUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!url) {
      alert('Please enter a URL');
      return;
    }
    try {
      await registerOverSizeList({ 
        url,
        adminName: shopName
      });
      setUrl('');
    } catch (err) {
      console.error('Error registering over size list:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteOverSizeList(id);
    } catch (err) {
      console.error('Error deleting over size list:', err);
    }
  };

  const handleSearchClear = () => {
    setSearchUrl('');
  };

  return (
    <>
      <Grid container spacing={3}>
        {/* Left Side - Table Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                รายการ Over Size
              </Typography>
              <TextField
                size="small"
                placeholder="Search by URL..."
                value={searchUrl}
                onChange={(e) => setSearchUrl(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchUrl && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleSearchClear}
                        edge="end"
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
              />
            </Box>
            <TableContainer sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 50 }}>No.</TableCell>
                    <TableCell sx={{ minWidth: 200 }}>URL</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Admin Name</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Date</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLists && filteredLists.length > 0 && filteredLists.map((item, index) => (
                    <TableRow key={item._id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Button
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: 'none' }}
                        >
                          {item.url}
                        </Button>
                      </TableCell>
                      <TableCell>
                        {item.admin && item.admin.nickname || '-'}
                      </TableCell>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton 
                            onClick={() => handleDelete(item.id)}
                            color="error"
                            size="small"
                            title="Delete"
                            disabled={loading}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredLists.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {searchUrl ? 'No results found for your search' : 'No over size items found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Right Side - Input Section */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Add New Over Size URL
            </Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="URL"
                variant="outlined"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                type="url"
                placeholder="Enter URL"
                sx={{ mb: 2 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !url}
                startIcon={<AddIcon />}
                fullWidth
              >
                {loading ? 'Adding...' : 'Add URL'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

export default OverSizeList;
